import { useRef, useState } from 'react';
import {
    BreakpointRangeProps,
    Container,
    Dialog,
    SearchInput,
    SearchInputProps,
    VisuallyHidden,
    withBreakpointRange,
} from '@optimus-web/core';
import { DefaultCurrencySection } from './components/DefaultCurrencySection';
import { FilteredCurrencySection } from './components/FilteredCurrencySection';
import { getCurrencyOptions } from './utils/getCurrencyOptions';
import { getSelectionCardGroupOptions } from './utils/getSelectionCardGroupOptions';
import { getFilteredCurrenciesBySearch } from './utils/getFilteredCurrenciesBySearch';
import { Currency, CurrencySelectDialogTexts } from './types';

// TODO FINTECH-9778: Once translation keys added for distributor and navigator, remove the Partial from the texts prop
export interface CurrencySelectDialogProps extends BreakpointRangeProps {
    currencies: Currency[];
    isActive: boolean;
    portalContainerId: string;
    propertyCurrencyCode?: string;
    selectedCurrencyCode: string;
    suggestedCurrencyCodes?: string[];
    texts: Partial<CurrencySelectDialogTexts>;
    showSearchInput?: boolean;
    onChange: (currencyCode: string) => void;
    onClose: () => void;
}

const BaseCurrencySelectDialog = ({
    breakpointRange,
    currencies,
    isActive,
    portalContainerId,
    propertyCurrencyCode,
    selectedCurrencyCode,
    suggestedCurrencyCodes = [],
    texts,
    showSearchInput = false,
    onChange,
    onClose,
}: CurrencySelectDialogProps) => {
    const dialogContentRef = useRef<HTMLDivElement>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);

    const [search, setSearch] = useState('');
    const searchTrimmed = search.trim();

    // TODO FINTECH-9778: Remove hardcoded texts once translation keys added for distributor and navigator
    const currencySelectDialogTexts: CurrencySelectDialogTexts = {
        dialogTitle: texts.dialogTitle ?? 'Select your currency',
        suggestedCurrenciesTitle: texts.suggestedCurrenciesTitle ?? 'Suggested for you',
        otherCurrenciesTitle: texts.otherCurrenciesTitle ?? 'All currencies',
        propertyCurrencyLabel: texts.propertyCurrencyLabel ?? 'Property',
        searchInputPlaceholder: texts.searchInputPlaceholder ?? 'Search currency by name, code, or symbol',
        searchInputAriaLabel: texts.searchInputAriaLabel ?? 'Currency filter search input',
        filteredCurrenciesAriaLabel: texts.filteredCurrenciesAriaLabel ?? 'Filtered currencies',
        filteredCurrenciesEmptyStatePrimary: texts.filteredCurrenciesEmptyStatePrimary ?? 'No currencies found',
        filteredCurrenciesEmptyStateSecondary:
            texts.filteredCurrenciesEmptyStateSecondary ??
            "We couldn't find any currencies for your search. Try another code, symbol, or name.",
        suggestedCurrenciesAriaLabel: texts.suggestedCurrenciesAriaLabel ?? 'Suggested currencies',
        otherCurrenciesAriaLabel: texts.otherCurrenciesAriaLabel ?? 'Other currencies',
        getFilteredCountForScreenReadersText: texts.getFilteredCountForScreenReadersText,
    };

    const { suggestedCurrencyOptions, otherCurrencyOptions } = getCurrencyOptions({
        currencies,
        selectedCurrencyCode,
        suggestedCurrencyCodes,
    });

    const filteredCurrencies = getFilteredCurrenciesBySearch(currencies, search);

    const suggestedCurrenciesCardGroupOptions = getSelectionCardGroupOptions({
        currencies: suggestedCurrencyOptions,
        propertyCurrencyCode,
        propertyCurrencyLabelText: currencySelectDialogTexts.propertyCurrencyLabel,
    });

    const otherCurrenciesCardGroupOptions = getSelectionCardGroupOptions({
        currencies: otherCurrencyOptions,
        propertyCurrencyCode,
        propertyCurrencyLabelText: currencySelectDialogTexts.propertyCurrencyLabel,
    });

    const filteredCurrenciesCardGroupOptions = getSelectionCardGroupOptions({
        currencies: filteredCurrencies,
        propertyCurrencyCode,
        propertyCurrencyLabelText: currencySelectDialogTexts.propertyCurrencyLabel,
    });

    const hasSuggestedCurrenciesCardGroupOptions =
        suggestedCurrenciesCardGroupOptions.length > 0 && searchTrimmed === '';

    const clearSearch = () => {
        setSearch('');
    };

    const handleSearchChange: SearchInputProps['onChange'] = (_e, value) => {
        if (typeof value === 'string') setSearch(value);
    };

    const handleCurrencyChange = (currencyCode: string | undefined) => {
        // For SelectionCardGroup we are using type="checkbox" so we need to prevent the user from unselecting the value
        if (currencyCode !== undefined) {
            onChange(currencyCode);
        }
        clearSearch();
    };

    const handleClose = () => {
        clearSearch();
        onClose();
    };

    return (
        <Dialog
            active={isActive}
            modal
            onClose={handleClose}
            size="extraLarge"
            title={currencySelectDialogTexts.dialogTitle}
            portalContainerId={portalContainerId}
            contentRef={dialogContentRef}
            defaultFocusElement={() => {
                if (showSearchInput && searchInputRef.current) return searchInputRef.current;
                return dialogContentRef.current?.querySelector<HTMLElement>('button[role="checkbox"]') ?? null;
            }}
        >
            <Container
                height="600px"
                padding={hasSuggestedCurrenciesCardGroupOptions ? ['0', '0', '100'] : ['50', '0']}
            >
                {showSearchInput && (
                    <Container padding={['0', '0', '300']}>
                        <SearchInput
                            ref={searchInputRef}
                            name="filter"
                            size="large"
                            value={search}
                            onSearch={() => {}}
                            onChange={handleSearchChange}
                            onClear={clearSearch}
                            clearable
                            placeholder={currencySelectDialogTexts.searchInputPlaceholder}
                            aria-label={currencySelectDialogTexts.searchInputAriaLabel}
                        />
                        {searchTrimmed.length > 0 &&
                            currencySelectDialogTexts.getFilteredCountForScreenReadersText != null && (
                                <VisuallyHidden role="status" aria-live="polite" aria-atomic="true">
                                    {currencySelectDialogTexts.getFilteredCountForScreenReadersText(
                                        filteredCurrencies.length
                                    )}
                                </VisuallyHidden>
                            )}
                    </Container>
                )}

                {showSearchInput && searchTrimmed.length > 0 ? (
                    <FilteredCurrencySection
                        filteredCurrenciesCardGroupOptions={filteredCurrenciesCardGroupOptions}
                        suggestedCurrenciesCardGroupOptions={suggestedCurrenciesCardGroupOptions}
                        breakpointRange={breakpointRange}
                        texts={currencySelectDialogTexts}
                        handleChange={handleCurrencyChange}
                    />
                ) : (
                    <DefaultCurrencySection
                        hasSuggestedCurrenciesCardGroupOptions={hasSuggestedCurrenciesCardGroupOptions}
                        suggestedCurrenciesCardGroupOptions={suggestedCurrenciesCardGroupOptions}
                        otherCurrenciesCardGroupOptions={otherCurrenciesCardGroupOptions}
                        selectedCurrencyCode={selectedCurrencyCode}
                        breakpointRange={breakpointRange}
                        texts={currencySelectDialogTexts}
                        handleChange={handleCurrencyChange}
                    />
                )}
            </Container>
        </Dialog>
    );
};

export const CurrencySelectDialog = withBreakpointRange<CurrencySelectDialogProps>(BaseCurrencySelectDialog);
